import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Sync as SyncIcon } from '@mui/icons-material';
import AIConfigService, { AIConfigRequest } from '../services/aiConfigService';

interface Module4ConfigAssistantProps {
  onConfigGenerated: (config: any) => void;
  initialPrompt?: string;
}

const Module4ConfigAssistant: React.FC<Module4ConfigAssistantProps> = ({
  onConfigGenerated,
  initialPrompt = '',
}) => {
  const [userPrompt, setUserPrompt] = useState(initialPrompt);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const handleGenerateConfig = async () => {
    if (!userPrompt.trim()) {
      setError('Please describe your batch keyword classification needs');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setExplanation(null);
    setSuggestions([]);

    try {
      const aiService = AIConfigService.getInstance();
      const request: AIConfigRequest = {
        prompt: `Generate a batch keyword classification config based on the following requirements:

${userPrompt}

The configuration should follow this structure:
{
  "config_id": "unique-id-for-config",
  "name": "Human-readable name",
  "description": "Detailed description of the classification's purpose",
  "topic_mapping": {
    "1": "Topic Name 1",
    "2": "Topic Name 2"
  },
  "prompt": {
    "system_prompt": "You are an expert in categorizing [domain] search queries. Analyze the query and return a JSON object classifying it into relevant categories.\n\nCategories:\n[Extract and list all categories from the user's description]\n\nYour response MUST be a properly formatted JSON object with these exact field names:\n{\n  \"category1\": boolean,\n  \"category2\": boolean,\n  ...\n}\n\nFor each category, set true if it applies to the query or false if it does not.",
    "output_format": "json",
    "temperature": 0.2,
    "max_tokens": 300
  },
  "results": {
    "output_format": "json",
    "output_regex": "",
    "result_columns": ["category1", "category2", "confidence"]
  },
  "version": 1,
  "is_active": true
}

Please:
1. Extract the main categories from the user's description
2. Generate a unique config_id based on the domain
3. Create a comprehensive system prompt that includes all identified categories
4. Set up the result columns to match the categories
5. Generate a complete batch keyword classification that matches these requirements.`
      };

      const response = await aiService.generateConfig(request);
      
      setExplanation(response.explanation);
      setSuggestions(response.suggestions);
      onConfigGenerated(response.config);
    } catch (err) {
      setError('Failed to generate batch keyword classification. Please try again.');
      console.error('Error generating batch keyword classification:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Batch Keyword Classification Assistant</CardTitle>
        <CardDescription>
          Get AI-powered suggestions to optimize your batch keyword classification
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); handleGenerateConfig(); }} className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Label htmlFor="userPrompt">Describe your classification requirements:</Label>
            <Textarea
              id="userPrompt"
              placeholder="E.g., I need to classify insurance keywords into categories like auto, home, life, and health..."
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              className="min-h-[100px]"
              disabled={isGenerating}
            />
          </div>
          
          <Button type="submit" disabled={isGenerating || !userPrompt} className="w-full">
            {isGenerating ? (
              <>
                <SyncIcon className="mr-2 h-4 w-4 animate-spin" />
                Generating Classification...
              </>
            ) : (
              'Generate Batch Keyword Classification'
            )}
          </Button>

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {explanation && (
            <Alert>
              <AlertTitle>AI Explanation</AlertTitle>
              <AlertDescription>{explanation}</AlertDescription>
            </Alert>
          )}

          {suggestions.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-medium">Suggestions:</h4>
              <ul className="list-disc pl-5 space-y-1">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="text-sm">{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default Module4ConfigAssistant; 